import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/hooks/useAuth';
import { Calendar, Clock, MapPin, CheckCircle2, AlertCircle, ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { toast } from 'sonner';

export default function MyAssignmentsView({ user, teams, onSelectAppointment }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchAssignments();
  }, [currentDate, statusFilter]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const month = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      const params = { month };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await api.get('/appointments/my-assignments', { params });
      setAssignments(response.data);
    } catch (error) {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (appointmentId) => {
    if (window.confirm('Mark this appointment as completed?')) {
      try {
        await api.patch(`/appointments/${appointmentId}/complete`);
        toast.success('Appointment marked as completed');
        fetchAssignments();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to complete appointment');
      }
    }
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getTeamInfo = (teamId) => {
    return teams.find(t => t.id === teamId) || { name: 'Unknown', color: '#64748b' };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const isPast = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr < today;
  };

  // Group assignments by date
  const groupedAssignments = assignments.reduce((groups, apt) => {
    const date = apt.date;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(apt);
    return groups;
  }, {});

  const sortedDates = Object.keys(groupedAssignments).sort();

  const scheduledCount = assignments.filter(a => a.status === 'scheduled').length;
  const completedCount = assignments.filter(a => a.status === 'completed').length;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">My Assignments</h2>
          <p className="text-slate-500 text-xs sm:text-sm">Appointments assigned to you or your teams</p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
          {/* Month Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={prevMonth} data-testid="my-assignments-prev-month" className="h-8 w-8 sm:h-10 sm:w-10">
              <ChevronLeft size={18} />
            </Button>
            <span className="text-sm sm:text-lg font-semibold min-w-[120px] sm:min-w-[160px] text-center">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <Button variant="outline" size="icon" onClick={nextMonth} data-testid="my-assignments-next-month" className="h-8 w-8 sm:h-10 sm:w-10">
              <ChevronRight size={18} />
            </Button>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-slate-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[120px] sm:w-[140px] h-8 sm:h-10 text-sm" data-testid="status-filter">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card>
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">Total</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-800">{assignments.length}</p>
              </div>
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">Scheduled</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-600">{scheduledCount}</p>
              </div>
              <AlertCircle className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-3 sm:pt-4 px-2 sm:px-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">Completed</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">{completedCount}</p>
              </div>
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-400 hidden sm:block" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assignments List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb]"></div>
        </div>
      ) : assignments.length === 0 ? (
        <Card>
          <CardContent className="py-8 sm:py-12 text-center">
            <Calendar className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No assignments for this month</p>
            <p className="text-slate-400 text-sm mt-1">
              {statusFilter !== 'all' ? 'Try changing the status filter' : 'Check back later for new assignments'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedDates.map(date => (
            <div key={date}>
              {/* Date Header */}
              <div className={`flex items-center gap-2 mb-3 ${isToday(date) ? 'text-[#2563eb]' : isPast(date) ? 'text-slate-400' : 'text-slate-700'}`}>
                <Calendar size={16} />
                <span className="font-semibold">{formatDate(date)}</span>
                {isToday(date) && <Badge className="bg-[#2563eb]">Today</Badge>}
                {isPast(date) && !isToday(date) && <Badge variant="secondary">Past</Badge>}
              </div>

              {/* Appointments for this date */}
              <div className="space-y-3 ml-6">
                {groupedAssignments[date].map(apt => {
                  const team = getTeamInfo(apt.team_id);
                  return (
                    <Card 
                      key={apt.id} 
                      className={`cursor-pointer hover:shadow-md transition-shadow ${apt.status === 'completed' ? 'bg-green-50/50 border-green-200' : ''}`}
                      onClick={() => onSelectAppointment(apt)}
                      data-testid={`assignment-card-${apt.id}`}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: team.color }}
                              />
                              <span className="text-sm text-slate-500">{team.name}</span>
                              {apt.status === 'completed' ? (
                                <Badge className="bg-green-600 text-white">
                                  <CheckCircle2 size={12} className="mr-1" />
                                  Completed
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-600 border-amber-300">
                                  Scheduled
                                </Badge>
                              )}
                            </div>
                            
                            <h3 className="font-semibold text-slate-800 mb-1">{apt.title}</h3>
                            
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <div className="flex items-center gap-1">
                                <Clock size={14} />
                                {apt.start_time} - {apt.end_time}
                              </div>
                              {apt.location && (
                                <div className="flex items-center gap-1">
                                  <MapPin size={14} />
                                  {apt.location}
                                </div>
                              )}
                            </div>

                            {apt.status === 'completed' && apt.completed_by_name && (
                              <p className="text-xs text-green-600 mt-2">
                                Completed by {apt.completed_by_name}
                              </p>
                            )}
                          </div>

                          {/* Action Button */}
                          {apt.status !== 'completed' && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleComplete(apt.id);
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              size="sm"
                              data-testid={`complete-btn-${apt.id}`}
                            >
                              <CheckCircle2 size={16} className="mr-1" />
                              Complete
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
